import { useRef, useState } from 'react';
import { useWeeklyResults } from '@/hooks/useWeeklyResults';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Download, Share2, Loader2 } from 'lucide-react';
import { format as formatDate, startOfWeek } from 'date-fns';
import html2canvas from 'html2canvas';

interface WinCardStats {
  contacts_done: number;
  followups_scheduled: number;
  meetings_set: number;
  proposals_sent: number;
  won: number;
  lost: number;
  reactivated: number;
}

interface WinCardProps {
  stats: WinCardStats;
  weekStart: Date;
  layout: 'story' | 'feed';
}

function WinCardCanvas({ stats, weekStart, layout }: WinCardProps) {
  const isStory = layout === 'story';
  const width = 1080;
  const height = isStory ? 1920 : 1350;

  return (
    <div
      style={{
        width: `${width}px`,
        height: `${height}px`,
        background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)',
        color: '#ffffff',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        padding: isStory ? '120px 80px' : '100px 80px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background pattern */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.05,
          backgroundImage: 'radial-gradient(circle at 2px 2px, #ffffff 1px, transparent 0)',
          backgroundSize: '40px 40px',
        }}
      />

      {/* Header */}
      <div style={{ position: 'relative', zIndex: 1 }}>
        <h1
          style={{
            fontSize: isStory ? '64px' : '56px',
            fontWeight: 700,
            marginBottom: '16px',
            letterSpacing: '-0.02em',
          }}
        >
          This Week
        </h1>
        <p
          style={{
            fontSize: isStory ? '28px' : '24px',
            color: '#a0a0a0',
            fontWeight: 400,
          }}
        >
          {formatDate(weekStart, 'MMM d')} -{' '}
          {formatDate(new Date(weekStart.getTime() + 6 * 24 * 60 * 60 * 1000), 'MMM d, yyyy')}
        </p>
      </div>

      {/* Stats Grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: isStory ? '32px' : '28px',
          position: 'relative',
          zIndex: 1,
          flex: 1,
          marginTop: isStory ? '60px' : '50px',
        }}
      >
        {[
          { label: 'Contacts', value: stats.contacts_done, icon: '📞' },
          { label: 'Follow-ups', value: stats.followups_scheduled, icon: '📅' },
          { label: 'Meetings', value: stats.meetings_set, icon: '🤝' },
          { label: 'Proposals', value: stats.proposals_sent, icon: '📄' },
          { label: 'Won', value: stats.won, icon: '🏆' },
          { label: 'Reactivated', value: stats.reactivated, icon: '🔄' },
        ].map((stat, idx) => (
          <div
            key={idx}
            style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '16px',
              padding: isStory ? '32px 24px' : '28px 20px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <div style={{ fontSize: isStory ? '40px' : '36px', marginBottom: '12px' }}>{stat.icon}</div>
            <div
              style={{
                fontSize: isStory ? '48px' : '42px',
                fontWeight: 700,
                marginBottom: '8px',
              }}
            >
              {stat.value}
            </div>
            <div
              style={{
                fontSize: isStory ? '20px' : '18px',
                color: '#a0a0a0',
                fontWeight: 400,
              }}
            >
              {stat.label}
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          marginTop: isStory ? '60px' : '50px',
          paddingTop: isStory ? '40px' : '32px',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <p
          style={{
            fontSize: isStory ? '20px' : '18px',
            color: '#666666',
            textAlign: 'center',
            fontWeight: 400,
          }}
        >
          Powered by Decision Sales OS
        </p>
      </div>
    </div>
  );
}

export function WinCardGenerator() {
  const { data: reports, isLoading } = useWeeklyResults(1);
  const [generating, setGenerating] = useState(false);
  const [layout, setLayout] = useState<'story' | 'feed'>('story');
  const cardRef = useRef<HTMLDivElement>(null);

  const currentWeek = reports?.[0];
  const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 });

  const fileName = `win-card-${layout}-${formatDate(weekStart, 'yyyy-MM-dd')}.png`;

  const renderCanvas = async () => {
    if (!cardRef.current) throw new Error('Missing cardRef');
    return html2canvas(cardRef.current, {
      width: 1080,
      height: layout === 'story' ? 1920 : 1350,
      scale: 1,
      backgroundColor: '#0a0a0a',
      useCORS: true,
    });
  };

  const handleGenerate = async () => {
    if (!currentWeek) return;
    setGenerating(true);
    try {
      const canvas = await renderCanvas();
      const dataUrl = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = fileName;
      link.href = dataUrl;
      link.click();
    } catch (error) {
      console.error('Failed to generate win card', error);
    } finally {
      setGenerating(false);
    }
  };

  const handleShare = async () => {
    if (!currentWeek) return;
    setGenerating(true);
    try {
      const canvas = await renderCanvas();

      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob returned null'))), 'image/png');
      });

      const file = new File([blob], fileName, { type: 'image/png' });

      if (typeof navigator.share === 'function' && navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: 'My Weekly Results',
          text: 'Check out my weekly sales results!',
        });
      } else {
        // Fallback: download
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.download = fileName;
        link.href = url;
        link.click();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Failed to share win card', error);
    } finally {
      setGenerating(false);
    }
  };

  if (isLoading || !currentWeek) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // CurrentWeek stats should match WinCardStats; cast safely
  const stats = (currentWeek.stats ?? {}) as WinCardStats;

  return (
    <Card>
      <CardContent className="p-6 space-y-6">
        <div>
          <h3 className="text-lg font-semibold mb-2">Shareable Win Card</h3>
          <p className="text-sm text-muted-foreground">Generate and share your weekly results</p>
        </div>

        {/* Format selector */}
        <div className="flex gap-2">
          <Button
            variant={layout === 'story' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setLayout('story')}
          >
            Story (1080x1920)
          </Button>
          <Button
            variant={layout === 'feed' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setLayout('feed')}
          >
            Feed (1080x1350)
          </Button>
        </div>

        {/* Offscreen render (NOT display:none) for html2canvas */}
        <div
          style={{
            position: 'fixed',
            left: -10000,
            top: 0,
            width: 1080,
            height: layout === 'story' ? 1920 : 1350,
          }}
          aria-hidden="true"
        >
          <div ref={cardRef}>
            <WinCardCanvas stats={stats} weekStart={weekStart} layout={layout} />
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <Button onClick={handleGenerate} disabled={generating} className="flex-1">
            {generating ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Download
          </Button>
          <Button onClick={handleShare} disabled={generating} variant="outline" className="flex-1">
            {generating ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Share2 className="w-4 h-4 mr-2" />
            )}
            Share
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
